/**
 * Rate Checker — Compare saved quote rates against live API rates
 * Shows a banner when rates have changed since the quote was saved
 */

/**
 * Check if a saved quote's rates are still current
 * @param {Object} quote - Saved quote with plans[] and rateSnapshotDate
 * @param {Function} calculateFn - Function to recalculate premiums with current API data
 * @returns {{ stale: boolean, changes: Object[] }} Changes array has { planId, savedPremium, livePremium, diff }
 */
export async function checkRates(quote, calculateFn) {
  // Only check if quote is older than 24 hours
  const snapshotAge = Date.now() - new Date(quote.rateSnapshotDate || quote.createdAt).getTime();
  const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;

  if (snapshotAge < TWENTY_FOUR_HOURS) {
    return { stale: false, changes: [] };
  }

  try {
    // Re-fetch live rates using the same inputs
    const liveResults = await calculateFn({
      dob: quote.clientDob,
      zipCode: quote.zipCode,
      coverageType: quote.coverageType,
      gender: quote.clientGender,
      tobacco: quote.clientTobacco,
      effectiveDate: quote.effectiveDate,
      householdIncome: quote.householdIncome,
      householdSize: quote.householdSize,
      spouse: quote.spouse,
      children: quote.children,
    });

    // Compare saved vs live premiums
    const changes = [];
    for (const savedPlan of quote.plans) {
      const livePlan = liveResults.find(p =>
        p.planId === savedPlan.planId && p.source === savedPlan.source
      );

      if (!livePlan) continue;

      const savedPremium = savedPlan.premium || savedPlan.calculatedPremium || 0;
      const livePremium = livePlan.premium || livePlan.calculatedPremium || 0;
      const diff = livePremium - savedPremium;

      // Flag if difference is more than $1
      if (Math.abs(diff) > 1) {
        changes.push({
          planId: savedPlan.planId,
          planName: savedPlan.planName || savedPlan.name,
          source: savedPlan.source,
          savedPremium,
          livePremium,
          diff,
          direction: diff > 0 ? 'increased' : 'decreased',
        });
      }
    }

    return {
      stale: changes.length > 0,
      changes,
    };
  } catch {
    // If rate check fails (API down, etc.), don't show stale banner
    return { stale: false, changes: [], error: true };
  }
}

/**
 * Render a rate change banner
 * @param {Object} rateResult - Result from checkRates()
 * @param {string} snapshotDate - ISO date string of when rates were captured
 * @returns {string} HTML string for the banner (empty string if not stale)
 */
export function renderRateBanner(rateResult, snapshotDate) {
  if (!rateResult.stale) return '';

  const dateStr = new Date(snapshotDate).toLocaleDateString('en-US', { timeZone: 'UTC' });
  const changeCount = rateResult.changes.length;
  const increased = rateResult.changes.filter(c => c.direction === 'increased').length;
  const decreased = rateResult.changes.filter(c => c.direction === 'decreased').length;

  let detail = '';
  if (increased > 0 && decreased > 0) {
    detail = `${increased} plan(s) increased, ${decreased} decreased`;
  } else if (increased > 0) {
    detail = `${increased} plan(s) have higher premiums`;
  } else {
    detail = `${decreased} plan(s) have lower premiums`;
  }

  return `
    <div class="wq-rate-banner wq-rate-banner--stale">
      <div class="wq-rate-banner__content">
        <strong>Rates may have changed</strong> since this quote was created on ${dateStr}.
        ${detail}.
      </div>
      <button class="wq-btn wq-btn--sm wq-btn--primary wq-rate-banner__recalc" id="wq-recalculate-btn">
        Recalculate
      </button>
    </div>
  `;
}

export const RateChecker = {
  checkRates,
  renderRateBanner,
};
